import './styles/ModuleSelectionsDetail.scss';

import { useQuery as useGQLQuery } from '@apollo/client';
import { Grid, Paper, Tab, Tabs } from '@mui/material';

import { useTranslation } from 'react-i18next';
import 'react-quill/dist/quill.bubble.css';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import MODULES_SELECTION_QUERY from 'gql/queries/ModulesService/getModuleSelectionById.gql';

import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Course } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';

import { ModuleSelection } from '../ModuleSelectionsList';
import { hasAtLeastStarted } from '../helper';
import { ObserverType, SubjectType } from '../models';
import { AllocationTab } from './tabs/AllocationTab';
import { PreviewTab } from './tabs/PreviewTab';
import { StudentList } from './tabs/StudentList';

export enum SelectionTabPanels {
  MODULE_PREVIEW = 'modulePreview',
  OCCUPANCY = 'occupancy',
  STUDENT_LIST = 'studentList'
}
interface Props {
  subjectType: SubjectType;
}

type GetModuleSelectionByIdQueryResult = { getModuleSelectionById: ModuleSelection };
export const ModuleSelectionsDetail = (props: Props) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, authorRoles } = useAuthContext();
  const aclHelper = new ACLHelper(currentUser);

  const [tab, setTab] = useQueryStringState<SelectionTabPanels>(
    'tab',
    SelectionTabPanels.MODULE_PREVIEW
  );

  const getModuleSelectionById = useGQLQuery<GetModuleSelectionByIdQueryResult, any>(
    MODULES_SELECTION_QUERY,
    {
      context: { clientName: 'federation' },
      variables: { id: parseInt(id!, 10) }
    }
  );

  usePageHeading(
    {
      heading:
        getModuleSelectionById?.data?.getModuleSelectionById?.title ||
        t(translations.common.loading()),
      headingProps: {
        back: location.state?.back ?? `/${props.subjectType.toLowerCase()}/module-selections`
      }
    },
    [getModuleSelectionById?.data]
  );

  function isAdminOfSelection(courses: Course[], moduleSelection: ModuleSelection) {
    return (
      (aclHelper.isSelectionAdmin(true) &&
      props.subjectType === SubjectType.Fwpm &&
      moduleSelection.courseId
        ? aclHelper
            .getSelectionAdminCourses(courses)
            .map(course => course.id)
            .includes(moduleSelection.courseId)
        : aclHelper.isSubjectSelectionAdmin(true, SubjectType.Awpm)) || aclHelper.isSuperUser()
    );
  }

  function isObserver(moduleSelection: ModuleSelection) {
    return !!moduleSelection.observers.some(
      o =>
        (o.observerType === ObserverType.USER && o.refId === currentUser.id) ||
        (o.observerType === ObserverType.ROLE &&
          authorRoles.some(authorRole => authorRole.id === o.refId.toString()))
    );
  }

  const { data: courses } = useCoursesQuery();

  return (
    <GQLGuard
      query={[getModuleSelectionById]}
      onError={error => {
        if (error.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(
            AlertActions.error(`${t(translations.pages.moduleSelectionsDetail.failedLoading())}`)
          );
        } else {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.moduleSelectionsDetail.failedLoading())}: ${error.message}`
            )
          );
        }
        navigate('/', { replace: true });
        return null;
      }}
    >
      {([data]: [GetModuleSelectionByIdQueryResult]) => (
        <Paper className="selection-detail">
          {hasAtLeastStarted(data.getModuleSelectionById) && (
            <Grid container borderBottom="1px solid lightgray">
              <Tabs
                value={tab}
                onChange={(_event, value: SelectionTabPanels) => setTab(value)}
                aria-label="preview/occupancy/studentlist tabs"
                indicatorColor="primary"
              >
                <Tab
                  value={SelectionTabPanels.MODULE_PREVIEW}
                  label={t(translations.pages.moduleSelectionsDetail.modulePreview.tabTitle())}
                />

                <Tab
                  value={SelectionTabPanels.OCCUPANCY}
                  label={t(translations.pages.moduleSelectionsDetail.occupancy.tabTitle())}
                />
                {(isAdminOfSelection(courses, data.getModuleSelectionById) ||
                  isObserver(data.getModuleSelectionById)) && (
                  <Tab
                    value={SelectionTabPanels.STUDENT_LIST}
                    label={t(translations.pages.moduleSelectionsDetail.studentList.tabTitle())}
                  />
                )}
              </Tabs>
            </Grid>
          )}
          <TabPanel value={tab} index={SelectionTabPanels.MODULE_PREVIEW}>
            <PreviewTab
              moduleSelection={new ModuleSelection(data.getModuleSelectionById)}
              subjectType={props.subjectType}
              isSelectionAdmin={aclHelper.isSelectionAdmin()}
              currentUser={currentUser}
              setTab={setTab}
            />
          </TabPanel>
          {hasAtLeastStarted(data.getModuleSelectionById) && (
            <>
              <TabPanel value={tab} index={SelectionTabPanels.OCCUPANCY}>
                <AllocationTab
                  moduleSelection={data.getModuleSelectionById}
                  subjectType={props.subjectType}
                  isSelectionAdmin={aclHelper.isSelectionAdmin() || aclHelper.isSuperUser()}
                  currentUser={currentUser}
                />
              </TabPanel>
              <TabPanel value={tab} index={SelectionTabPanels.STUDENT_LIST}>
                <StudentList
                  moduleSelection={new ModuleSelection(data.getModuleSelectionById)}
                  subjectType={props.subjectType}
                />
              </TabPanel>
            </>
          )}
        </Paper>
      )}
    </GQLGuard>
  );
};

const TabPanel = props => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
};
